import { useState, useMemo, useRef, useCallback, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Typography,
  Chip,
  InputAdornment,
  Grid,
  Alert,
  Card,
  Autocomplete,
  Avatar,
  CircularProgress,
  Tooltip,
  Switch,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import CloseIcon from "@mui/icons-material/Close";
import { retailPosApi } from "./services/retailPosApi";
import {
  buildImagenesPayload,
  getProductImageUrls,
  MAX_PRODUCT_IMAGES,
  normalizeProductoImagenes,
} from "./utils/productImages";
import { ExportDataButton } from "./components/ExportDataDialog";
import { calcSaldoValoradoLinea } from "./utils/exportRetailData";

const productoVacio = {
  nombre: "",
  marca: "",
  producto_base: "",
  categoria: "",
  temporada: "",
  corte: "",
  codigo_barras: "",
  sku: "",
  talla: "",
  color: "",
  precio_compra: "",
  precio_venta: "",
  precio_minimo: "",
  stock: "",
};

export default function Inventario({ store }) {
  const [busqueda, setBusqueda] = useState("");
  const [openForm, setOpenForm] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(productoVacio);
  const [error, setError] = useState("");
  const [mostrarEliminados, setMostrarEliminados] = useState(false);

  // Imágenes del producto [{ key, url }]
  const [imagenes, setImagenes] = useState([]);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [storagePolicy, setStoragePolicy] = useState({
    uploadEnabled: false,
    limitBytes: 500 * 1024 * 1024,
    totalBytes: 0,
    loading: true,
  });
  const fileInputRef = useRef(null);

  const refreshStoragePolicy = useCallback(() => {
    setStoragePolicy((prev) => ({ ...prev, loading: true }));
    retailPosApi
      .getStorageUsage()
      .then((res) => {
        setStoragePolicy({
          uploadEnabled: Boolean(res?.uploadEnabled),
          limitBytes:
            Number(res?.limitBytes) > 0
              ? Number(res.limitBytes)
              : 500 * 1024 * 1024,
          totalBytes: Number(res?.totalBytes ?? 0),
          loading: false,
        });
      })
      .catch(() => {
        setStoragePolicy((prev) => ({ ...prev, loading: false }));
      });
  }, []);

  useEffect(() => {
    refreshStoragePolicy();
  }, [refreshStoragePolicy]);

  const productosFiltrados = useMemo(() => {
    if (!mostrarEliminados) {
      return store.buscarProductos(busqueda);
    }
    return store.buscarProductosInactivos(busqueda);
  }, [store, busqueda, mostrarEliminados]);

  const saldoValorado = useMemo(() => {
    const productos = (store.productos || []).filter((p) => p.activo !== false);
    return productos.reduce((acc, p) => acc + calcSaldoValoradoLinea(p, store), 0);
  }, [store.productos, store.obtenerCostoPromedioProducto]);

  const opcionesMarca = useMemo(() => {
    const values = (store.productos || [])
      .map((p) => String(p.marca || "").trim())
      .filter(Boolean);
    return [...new Set(values)].sort((a, b) =>
      a.localeCompare(b, "es", { sensitivity: "base" })
    );
  }, [store.productos]);

  const opcionesProductoBase = useMemo(() => {
    const values = (store.productos || [])
      .map((p) => String(p.producto_base || "").trim())
      .filter(Boolean);
    return [...new Set(values)].sort((a, b) =>
      a.localeCompare(b, "es", { sensitivity: "base" })
    );
  }, [store.productos]);

  const opcionesCategoria = useMemo(() => {
    const values = (store.productos || [])
      .map((p) => String(p.categoria || "").trim())
      .filter(Boolean);
    return [...new Set(values)].sort((a, b) =>
      a.localeCompare(b, "es", { sensitivity: "base" })
    );
  }, [store.productos]);

  const opcionesTalla = useMemo(() => {
    const values = (store.productos || [])
      .map((p) => String(p.talla || "").trim())
      .filter(Boolean);
    return [...new Set(values)].sort((a, b) =>
      a.localeCompare(b, "es", { sensitivity: "base" })
    );
  }, [store.productos]);

  const opcionesColor = useMemo(() => {
    const values = (store.productos || [])
      .map((p) => String(p.color || "").trim())
      .filter(Boolean);
    return [...new Set(values)].sort((a, b) =>
      a.localeCompare(b, "es", { sensitivity: "base" })
    );
  }, [store.productos]);

  const productoEditando = useMemo(() => {
    if (!editando) return null;
    return store.productos.find((p) => p.id === editando) || null;
  }, [editando, store.productos]);

  const bloquearEdicionStockInicial = useMemo(() => {
    if (!editando) return false;

    const tieneDetalleCompra = (store.detalleCompras || []).some(
      (d) => d.producto_id === editando
    );
    const tieneMovimientoCompra = (store.movimientosInventario || []).some(
      (m) => m.producto_id === editando && m.tipo === "COMPRA"
    );

    return tieneDetalleCompra || tieneMovimientoCompra;
  }, [editando, store.detalleCompras, store.movimientosInventario]);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleAutoChange = (field) => (_, newValue) => {
    setForm((prev) => ({ ...prev, [field]: newValue || "" }));
  };

  const handleAutoInputChange = (field) => (_, newInputValue) => {
    setForm((prev) => ({ ...prev, [field]: newInputValue || "" }));
  };

  const resetImageState = () => {
    setImagenes([]);
    setUploadError("");
  };

  const loadImagenesFromProducto = (producto) => normalizeProductoImagenes(producto);

  const abrirCrear = () => {
    setEditando(null);
    setForm(productoVacio);
    setError("");
    resetImageState();
    setOpenForm(true);
  };

  const abrirEditar = (producto) => {
    setEditando(producto.id);
    setForm({
      nombre: producto.nombre,
      marca: producto.marca,
      producto_base: producto.producto_base,
      categoria: producto.categoria,
      temporada: producto.temporada,
      corte: producto.corte,
      codigo_barras: producto.codigo_barras,
      sku: producto.sku,
      talla: producto.talla,
      color: producto.color,
      precio_compra: producto.precio_compra,
      precio_venta: producto.precio_venta,
      precio_minimo: producto.precio_minimo,
      stock: producto.stock,
    });
    setError("");
    setImagenes(loadImagenesFromProducto(producto));
    setUploadError("");
    setOpenForm(true);
  };

  const handleImageSelect = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (!file) return;

    if (imagenes.length >= MAX_PRODUCT_IMAGES) {
      setUploadError(`Máximo ${MAX_PRODUCT_IMAGES} fotos por producto.`);
      return;
    }

    if (!storagePolicy.uploadEnabled) {
      setUploadError(
        "La carga de fotos no está habilitada en tu plan. Contacta al administrador."
      );
      return;
    }

    const remaining = storagePolicy.limitBytes - storagePolicy.totalBytes;
    if (remaining <= 0) {
      setUploadError("Alcanzaste el límite de almacenamiento de imágenes.");
      return;
    }

    const MAX_MB = 5;
    if (file.size > remaining) {
      setUploadError(
        `No hay espacio suficiente (disponible: ${(remaining / (1024 * 1024)).toFixed(1)} MB).`
      );
      return;
    }

    if (file.size > MAX_MB * 1024 * 1024) {
      setUploadError(`El archivo supera ${MAX_MB} MB.`);
      return;
    }
    if (!file.type.startsWith("image/")) {
      setUploadError("Solo se permiten imágenes (JPEG, PNG, WebP).");
      return;
    }

    const localPreview = URL.createObjectURL(file);
    setUploadError("");
    setUploadingImage(true);

    try {
      const res = await retailPosApi.uploadProductImage(file);
      const url = res.signedUrl || res.url || localPreview;
      setImagenes((prev) => [
        ...prev,
        { key: res.key || "", url },
      ]);
      if (res?.storage) {
        setStoragePolicy((prev) => ({
          ...prev,
          totalBytes: Number(res.storage.totalBytes ?? prev.totalBytes),
          limitBytes: Number(res.storage.limitBytes) > 0 ? Number(res.storage.limitBytes) : prev.limitBytes,
          uploadEnabled: res.storage.uploadEnabled !== undefined ? Boolean(res.storage.uploadEnabled) : prev.uploadEnabled,
        }));
      } else {
        refreshStoragePolicy();
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Error al subir imagen";
      setUploadError(msg);
    } finally {
      setUploadingImage(false);
    }
  }, [imagenes.length, refreshStoragePolicy, storagePolicy.limitBytes, storagePolicy.totalBytes, storagePolicy.uploadEnabled]);

  const quitarImagen = (index) => {
    setImagenes((prev) => prev.filter((_, i) => i !== index));
  };

  const toggleMostrarEnCatalogo = (p) => {
    store.editarProducto(p.id, {
      mostrarEnCatalogo: p.mostrarEnCatalogo === false,
    });
  };

  const validarForm = () => {
    if (!form.nombre.trim()) return "El nombre es obligatorio";
    if (!form.codigo_barras.trim()) return "El código de barras es obligatorio";
    if (!form.precio_venta || parseFloat(form.precio_venta) <= 0)
      return "El precio de venta debe ser mayor a 0";
    if (
      form.precio_minimo !== "" &&
      parseFloat(form.precio_minimo) > parseFloat(form.precio_venta)
    ) {
      return "Precio mínimo no puede ser mayor al precio de venta";
    }
    if (form.stock !== "" && parseInt(form.stock) < 0)
      return "El stock no puede ser negativo";

    // Verificar código único (excluyendo el producto actual si se está editando)
    const existente = store.productos.find(
      (p) => p.codigo_barras === form.codigo_barras.trim() && p.id !== editando
    );
    if (existente)
      return "Ya existe un producto con ese código de barras";

    return null;
  };

  const handleGuardar = () => {
    const err = validarForm();
    if (err) {
      setError(err);
      return;
    }

    const base =
      editando && bloquearEdicionStockInicial
        ? { ...form, stock: productoEditando?.stock ?? form.stock }
        : form;

    const imgsPayload = buildImagenesPayload(imagenes);
    const firstKey = imgsPayload[0]?.key || "";
    const firstUrl = imagenes[0]?.url || "";
    const payload = {
      ...base,
      imagenes: imgsPayload,
      imagenKey: firstKey,
      imagenUrl: firstUrl,
    };

    if (editando) {
      store.editarProducto(editando, payload);
    } else {
      store.agregarProducto(payload);
    }

    setOpenForm(false);
    setForm(productoVacio);
    setEditando(null);
    resetImageState();
  };

  const handleEliminar = (id) => {
    if (window.confirm("¿Desactivar este producto del inventario?")) {
      store.desactivarProducto(id);
    }
  };

  return (
    <Box>
      {/* Header */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "center" }}
        spacing={2}
        mb={3}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1, minWidth: 300 }}>
          <TextField
            placeholder="Buscar por nombre o código de barras..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            size="small"
            sx={{ flex: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Button
            size="small"
            variant="text"
            onClick={() => setMostrarEliminados(!mostrarEliminados)}
            sx={{
              opacity: mostrarEliminados ? 0.9 : 0.5,
              transition: "opacity 0.2s",
              "&:hover": { opacity: 0.8 },
              whiteSpace: "nowrap",
            }}
          >
            {mostrarEliminados ? "Ocultar" : "Ver"}  eliminados
          </Button>
        </Stack>
        <Stack direction="row" spacing={1}>
          <ExportDataButton
            dataset="inventario"
            store={store}
            inventarioProductos={productosFiltrados}
            label="Exportar"
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={abrirCrear}
          >
            Nuevo Producto
          </Button>
        </Stack>
      </Stack>

      {!mostrarEliminados && (
        <Paper
          variant="outlined"
          sx={{
            px: 2.5,
            py: 1.5,
            mb: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1,
            bgcolor: "grey.50",
          }}
        >
          <Typography variant="body2" color="text.secondary" fontWeight={600}>
            Saldo valorado
          </Typography>
          <Typography variant="h6" fontWeight={700}>
            {store.config.moneda} {saldoValorado.toFixed(2)}
          </Typography>
        </Paper>
      )}

      {/* Info cuando se muestran eliminados */}
      {mostrarEliminados && productosFiltrados.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No hay productos desactivados. Desactiva productos para verlos aquí.
        </Alert>
      )}

      {/* Alerta de stock bajo */}
      {!mostrarEliminados && store.productosBajoStock.length > 0 && (
        <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 2 }}>
          {store.productosBajoStock.length} producto(s) con stock bajo
        </Alert>
      )}

      {/* Tabla */}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ "& th": { fontWeight: 700 } }}>
              <TableCell width={52}></TableCell>
              <TableCell>Producto</TableCell>
              <TableCell>Marca</TableCell>
              <TableCell>SKU</TableCell>
              <TableCell>Código</TableCell>
              <TableCell>Categoría</TableCell>
              <TableCell>Temporada</TableCell>
              <TableCell>Talla</TableCell>
              <TableCell>Color</TableCell>
              <TableCell align="right">C. Promedio</TableCell>
              <TableCell align="right">P. Venta</TableCell>
              <TableCell align="right">P. Mínimo</TableCell>
              <TableCell align="center">Stock</TableCell>
              <TableCell align="right">Saldo valorado</TableCell>
              <TableCell align="center" sx={{ minWidth: 88 }}>
                Catálogo
              </TableCell>
              <TableCell align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {productosFiltrados.length === 0 ? (
              <TableRow>
                <TableCell colSpan={16} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    {busqueda
                      ? "No se encontraron productos"
                      : "Aún no hay productos. ¡Agrega el primero!"}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              productosFiltrados.map((p) => {
                const thumbUrl = getProductImageUrls(p)[0];
                const saldoLinea = calcSaldoValoradoLinea(p, store);
                return (
                <TableRow key={p.id} hover sx={{ opacity: p.activo ? 1 : 0.5 }}>
                  <TableCell sx={{ py: 0.5 }}>
                    <Tooltip title={thumbUrl ? "Ver imagen" : "Sin imagen"}>
                      <Avatar
                        src={thumbUrl || undefined}
                        variant="rounded"
                        sx={{ width: 40, height: 40, bgcolor: "grey.100", cursor: thumbUrl ? "pointer" : "default" }}
                        onClick={() => thumbUrl && window.open(thumbUrl, "_blank")}
                      >
                        <PhotoCameraIcon sx={{ fontSize: 18, color: "grey.400" }} />
                      </Avatar>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <div>
                        <Typography variant="body2" fontWeight={600}>
                          {p.nombre}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Base: {p.producto_base || "-"}
                        </Typography>
                      </div>
                      {!p.activo && (
                        <Chip
                          label="Desactivado"
                          size="small"
                          variant="outlined"
                          color="error"
                          sx={{ height: 20 }}
                        />
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>{p.marca || "-"}</TableCell>
                  <TableCell>
                    <Typography variant="caption" fontFamily="monospace">
                      {p.sku || "-"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontFamily="monospace">
                      {p.codigo_barras}
                    </Typography>
                  </TableCell>
                  <TableCell>{p.categoria || "-"}</TableCell>
                  <TableCell>{p.temporada || "-"}</TableCell>
                  <TableCell>{p.talla || "—"}</TableCell>
                  <TableCell>{p.color || "—"}</TableCell>
                  <TableCell align="right">
                    {store.config.moneda}{" "}
                    {(store.obtenerCostoPromedioProducto
                      ? store.obtenerCostoPromedioProducto(p.id)
                      : p.precio_compra
                    ).toFixed(2)}
                  </TableCell>
                  <TableCell align="right">
                    {store.config.moneda}{" "}
                    {p.precio_venta.toFixed(2)}
                  </TableCell>
                  <TableCell align="right">
                    {store.config.moneda}{" "}
                    {(p.precio_minimo || 0).toFixed(2)}
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={p.stock}
                      size="small"
                      color={
                        p.stock <= 0
                          ? "error"
                          : p.stock <= store.config.stockMinimo
                          ? "warning"
                          : "success"
                      }
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    {store.config.moneda} {saldoLinea.toFixed(2)}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={p.mostrarEnCatalogo !== false ? "Visible en catálogo" : "Oculto en catálogo"}>
                      <Switch
                        size="small"
                        checked={p.mostrarEnCatalogo !== false}
                        onChange={() => toggleMostrarEnCatalogo(p)}
                        onClick={(e) => e.stopPropagation()}
                        inputProps={{ "aria-label": "Mostrar en catálogo" }}
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => abrirEditar(p)}
                      color="primary"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleEliminar(p.id)}
                      color="error"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
        {productosFiltrados.length} producto(s) en inventario
      </Typography>

      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid item xs={12} md={8}>
          <Card variant="outlined" sx={{ p: 2 }}>
            <Typography fontWeight={700} mb={1}>
              Matriz de variantes por producto base
            </Typography>
            {store.variantesPorBase.length === 0 ? (
              <Typography color="text.secondary" py={2}>
                Sin variantes disponibles.
              </Typography>
            ) : (
              <Stack spacing={2}>
                {store.variantesPorBase.map((base) => {
                  const tallas = base.tallas;
                  return (
                    <Box key={base.producto_base}>
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        justifyContent="space-between"
                        mb={1}
                      >
                        <Typography fontWeight={700}>{base.producto_base}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {base.categoria} | {base.temporada} | Stock total {base.totalStock}
                        </Typography>
                      </Stack>
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Color</TableCell>
                              {tallas.map((talla) => (
                                <TableCell key={`${base.producto_base}-${talla}`} align="center">
                                  {talla}
                                </TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {base.colores.map((color) => (
                              <TableRow key={`${base.producto_base}-${color}`}>
                                <TableCell>{color}</TableCell>
                                {tallas.map((talla) => {
                                  const variant = base.variantes.find(
                                    (v) => v.color === color && v.talla === talla
                                  );
                                  const stock = variant?.stock || 0;
                                  return (
                                    <TableCell
                                      key={`${base.producto_base}-${color}-${talla}`}
                                      align="center"
                                    >
                                      <Chip
                                        label={stock}
                                        size="small"
                                        color={
                                          stock <= 0
                                            ? "error"
                                            : stock <= store.config.stockMinimo
                                            ? "warning"
                                            : "success"
                                        }
                                        variant="outlined"
                                      />
                                    </TableCell>
                                  );
                                })}
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  );
                })}
              </Stack>
            )}
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card variant="outlined" sx={{ p: 2, height: "100%" }}>
            <Typography fontWeight={700} mb={1}>
              Reposición sugerida
            </Typography>
            {store.reposicionSugerida.length === 0 ? (
              <Typography color="text.secondary" py={2}>
                Sin alertas de reposición por ahora.
              </Typography>
            ) : (
              <Stack spacing={1.25}>
                {store.reposicionSugerida.slice(0, 12).map((r) => (
                  <Paper
                    key={r.producto_id}
                    variant="outlined"
                    sx={{ p: 1.25, borderColor: r.urgente ? "error.main" : "divider" }}
                  >
                    <Typography variant="body2" fontWeight={700}>
                      {r.nombre}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {r.talla || "UNICA"} / {r.color || "SIN COLOR"}
                    </Typography>
                    <Stack direction="row" justifyContent="space-between" mt={0.5}>
                      <Typography variant="caption">Stock: {r.stock}</Typography>
                      <Typography variant="caption">
                        Cobertura: {r.cobertura_dias === 999 ? "N/A" : `${r.cobertura_dias.toFixed(1)}d`}
                      </Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" mt={0.5}>
                      <Chip
                        size="small"
                        label={r.urgente ? "Urgente" : "Sugerido"}
                        color={r.urgente ? "error" : "warning"}
                      />
                      <Typography variant="body2" fontWeight={700}>
                        +{r.sugerido}
                      </Typography>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            )}
          </Card>
        </Grid>
      </Grid>

      {/* Modal Crear/Editar */}
      <Dialog
        open={openForm}
        onClose={() => setOpenForm(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editando ? "Editar Producto" : "Nuevo Producto"}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2, mt: 1 }}>
              {error}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            {/* Image upload (premium) */}
            <Grid item xs={12}>
              {!storagePolicy.loading && !storagePolicy.uploadEnabled ? (
                <Alert severity="info" sx={{ mb: 0 }}>
                  Las fotos de producto son una función premium. Pide al administrador que active &quot;Permitir subir fotos&quot; en tu cuenta POS Retail.
                </Alert>
              ) : (
                <>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    style={{ display: "none" }}
                    onChange={handleImageSelect}
                    disabled={!storagePolicy.uploadEnabled || storagePolicy.loading}
                  />
                  <Stack spacing={1.5}>
                    {imagenes.length > 0 && (
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {imagenes.map((img, idx) => (
                          <Box key={`${img.key}-${idx}`} sx={{ position: "relative" }}>
                            <Avatar
                              src={img.url || undefined}
                              variant="rounded"
                              sx={{ width: 72, height: 72, bgcolor: "grey.100", border: "1px solid", borderColor: "divider" }}
                            />
                            {!uploadingImage && (
                              <IconButton
                                size="small"
                                onClick={() => quitarImagen(idx)}
                                sx={{
                                  position: "absolute",
                                  top: -6,
                                  right: -6,
                                  bgcolor: "background.paper",
                                  boxShadow: 1,
                                  "&:hover": { bgcolor: "grey.100" },
                                }}
                                aria-label="Quitar foto"
                              >
                                <CloseIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            )}
                          </Box>
                        ))}
                      </Stack>
                    )}
                    <Box>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={uploadingImage ? <CircularProgress size={14} /> : <PhotoCameraIcon />}
                        disabled={
                          uploadingImage
                          || storagePolicy.loading
                          || !storagePolicy.uploadEnabled
                          || storagePolicy.totalBytes >= storagePolicy.limitBytes
                          || imagenes.length >= MAX_PRODUCT_IMAGES
                        }
                        onClick={() => fileInputRef.current?.click()}
                      >
                        {uploadingImage
                          ? "Subiendo…"
                          : imagenes.length > 0
                          ? "Agregar otra foto"
                          : "Agregar foto"}
                      </Button>
                      <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                        JPEG, PNG o WebP · máx. 5 MB · hasta {MAX_PRODUCT_IMAGES} fotos
                        {!storagePolicy.loading && storagePolicy.uploadEnabled && (
                          <> · almacenamiento {(storagePolicy.totalBytes / (1024 * 1024)).toFixed(1)} / {(storagePolicy.limitBytes / (1024 * 1024)).toFixed(0)} MB</>
                        )}
                      </Typography>
                      {uploadError && (
                        <Typography variant="caption" color="error" display="block">{uploadError}</Typography>
                      )}
                    </Box>
                  </Stack>
                </>
              )}
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Nombre del producto"
                name="nombre"
                value={form.nombre}
                onChange={handleChange}
                fullWidth
                required
                autoFocus
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                freeSolo
                options={opcionesMarca}
                value={form.marca || ""}
                inputValue={form.marca || ""}
                onChange={handleAutoChange("marca")}
                onInputChange={handleAutoInputChange("marca")}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Marca"
                    fullWidth
                    placeholder="Ej: Nike, Zara, Levi's"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                freeSolo
                options={opcionesProductoBase}
                value={form.producto_base || ""}
                inputValue={form.producto_base || ""}
                onChange={handleAutoChange("producto_base")}
                onInputChange={handleAutoInputChange("producto_base")}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Producto base"
                    fullWidth
                    placeholder="Ej: Polera Oversize"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <Autocomplete
                freeSolo
                options={opcionesCategoria}
                value={form.categoria || ""}
                inputValue={form.categoria || ""}
                onChange={handleAutoChange("categoria")}
                onInputChange={handleAutoInputChange("categoria")}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Categoría"
                    fullWidth
                    placeholder="Tops, Jeans"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                label="Temporada"
                name="temporada"
                value={form.temporada}
                onChange={handleChange}
                fullWidth
                placeholder="FW26"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Código de barras"
                name="codigo_barras"
                value={form.codigo_barras}
                onChange={handleChange}
                fullWidth
                required
                placeholder="Escanea o escribe el código"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="SKU (opcional, se autogenera)"
                name="sku"
                value={form.sku}
                onChange={handleChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Corte"
                name="corte"
                value={form.corte}
                onChange={handleChange}
                fullWidth
                placeholder="Regular, Oversize"
              />
            </Grid>
            <Grid item xs={6}>
              <Autocomplete
                freeSolo
                options={opcionesTalla}
                value={form.talla || ""}
                inputValue={form.talla || ""}
                onChange={handleAutoChange("talla")}
                onInputChange={handleAutoInputChange("talla")}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Talla"
                    fullWidth
                    placeholder="S, M, L, XL..."
                  />
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <Autocomplete
                freeSolo
                options={opcionesColor}
                value={form.color || ""}
                inputValue={form.color || ""}
                onChange={handleAutoChange("color")}
                onInputChange={handleAutoInputChange("color")}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Color"
                    fullWidth
                  />
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Precio de compra"
                name="precio_compra"
                value={form.precio_compra}
                onChange={handleChange}
                fullWidth
                type="number"
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Precio de venta"
                name="precio_venta"
                value={form.precio_venta}
                onChange={handleChange}
                fullWidth
                type="number"
                required
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Precio mínimo"
                name="precio_minimo"
                value={form.precio_minimo}
                onChange={handleChange}
                fullWidth
                type="number"
                inputProps={{ min: 0, step: 0.01 }}
                helperText="Protección de margen"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Stock inicial"
                name="stock"
                value={form.stock}
                onChange={handleChange}
                fullWidth
                type="number"
                disabled={Boolean(editando && bloquearEdicionStockInicial)}
                inputProps={{ min: 0 }}
                helperText={
                  editando && bloquearEdicionStockInicial
                    ? "No editable: este producto ya tiene compras registradas. Ajusta stock desde Compras o movimientos de inventario."
                    : ""
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpenForm(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleGuardar}>
            {editando ? "Guardar cambios" : "Crear producto"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
